import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import React from "react";
import { TbCheck, TbChevronDown } from "react-icons/tb";
import styled from "styled-components";
import { Caption1, Caption2, Subhead } from "../styles/typography";

const Layout = styled.div`
  gap: 16px;
  align-items: center;
  display: flex;
  flex-direction: column-reverse;
`;

const FieldColumn = styled.div`
  display: grid;
  grid-auto-flow: row;
  gap: 4px;
  width: 100%;
`;

const FieldLabel = styled(Caption1)`
  color: rgb(${(props) => props.theme.colors.neutral200});
  margin-left: 8px;
  text-transform: uppercase;
`;

const Trigger = styled(DropdownMenu.Trigger)`
  box-sizing: border-box;
  width: 100%;
  padding: 16px 24px;
  border: 2px solid
    rgb(
      ${(props) =>
        props.$error
          ? props.theme.colors.error400
          : props.theme.colors.neutral100}
    );
  border-radius: 8px;
  background: rgb(${(props) => props.theme.colors.white});
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  cursor: pointer;

  &:focus,
  &[data-state="open"] {
    border: 2px solid
      rgb(
        ${(props) =>
          props.$error
            ? props.theme.colors.error400
            : props.theme.colors.neutral600}
      );
    outline: none;
  }
`;

const TriggerLabel = styled(Subhead)`
  color: rgb(
    ${(props) =>
      props.$placeholder
        ? props.theme.colors.neutral200
        : props.theme.colors.neutral600}
  );
`;

const Content = styled(DropdownMenu.Content)`
  box-sizing: border-box;
  min-width: var(--radix-dropdown-menu-trigger-width);
  max-height: 320px;
  overflow-y: auto;
  background: rgb(${(props) => props.theme.colors.white});
  border: 2px solid rgb(${(props) => props.theme.colors.neutral150});
  border-radius: 8px;
  padding: 8px;
  z-index: 1000;
`;

const Item = styled(DropdownMenu.Item)`
  box-sizing: border-box;
  padding: 12px 16px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  cursor: pointer;
  outline: none;

  &[data-highlighted] {
    background: rgb(${(props) => props.theme.colors.offWhite});
  }
`;

const SwatchFrame = styled.div`
  box-sizing: border-box;
  width: 100%;
  min-height: 600px;
  border-radius: 8px;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: ${(p) => p.theme.shadows.shadow100()};
  background: ${(p) => p.$hex || "transparent"};

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    display: block;
  }

  @media screen and (max-width: 599px) {
    min-height: 80vw;
  }
`;

const PlaceholderLabel = styled(Caption1)`
  color: rgb(${(props) => props.theme.colors.neutral200});
  text-align: center;
  padding: 0 8px;
`;

const ErrorMessage = styled(Caption2)`
  color: rgb(${(props) => props.theme.colors.error400});
  margin: 2px 4px;
`;

/**
 * @param {{
 *  label?: string,
 *  options: {id:string,label:string,hex?:string}[],
 *  swatchImageUrl?: string,
 *  value: string,
 *  onChange: (value:string)=>void,
 *  errorCondition?: boolean,
 *  errorMessage?: string,
 * }} props
 */
const ColorPickerField = ({
  label,
  options,
  swatchImageUrl,
  value,
  onChange,
  errorCondition,
  errorMessage,
  style,
}) => {
  const selectedOption = options.find((option) => option.id === value);

  return (
    <Layout style={style}>
      <FieldColumn>
        {label && <FieldLabel>{label}</FieldLabel>}
        <DropdownMenu.Root>
          <Trigger $error={errorCondition} type="button">
            <TriggerLabel $placeholder={!selectedOption}>
              {selectedOption ? selectedOption.label : "Select a color"}
            </TriggerLabel>
            <TbChevronDown size={18} />
          </Trigger>
          <DropdownMenu.Portal>
            <Content align="start" sideOffset={4}>
              {options.map((option) => (
                <Item key={option.id} onSelect={() => onChange(option.id)}>
                  <Subhead>{option.label}</Subhead>
                  {value === option.id && <TbCheck size={18} />}
                </Item>
              ))}
            </Content>
          </DropdownMenu.Portal>
        </DropdownMenu.Root>
        {errorCondition && errorMessage && (
          <ErrorMessage>{errorMessage}</ErrorMessage>
        )}
      </FieldColumn>

      <SwatchFrame $hex={selectedOption?.hex}>
        {selectedOption?.hex ? null : swatchImageUrl ? (
          <img src={swatchImageUrl} alt="Color swatch reference" />
        ) : (
          <PlaceholderLabel>Preview</PlaceholderLabel>
        )}
      </SwatchFrame>
    </Layout>
  );
};

export default ColorPickerField;
